import { Box, Button, HStack, IconButton, Text, useToast, useToken, VStack } from 'native-base';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useQueryClient } from 'react-query';

import CPAlertDialog from 'app/components/CPAlertDialog';
import CustomHeaderBackButton from 'app/components/CustomHeaderBackButton/CustomHeaderBackButton';
import { mismatchProductLineup } from 'app/components/EntitlementsList/EntitlementsList';
import FixedBottom from 'app/components/FixedBottom';
import { PurchasesRefresh, TicketOutlinePlus } from 'app/components/icons';
import PurchasesEmptyState from 'app/components/PurchasesEmptyState';
import PurchasesManualAddSheet from 'app/components/PurchasesManualAddSheet';
import PurchasesTripsList from 'app/components/PurchasesTripsList';
import ResponsiveContainer from 'app/components/ResponsiveContainer';
import Screen from 'app/components/Screen';
import useAuth from 'app/hooks/useAuth';
import useGuest from 'app/hooks/useGuest';
import useGuestDataQuery from 'app/hooks/useGuestDataQuery';
import useGuestDataRefreshControl from 'app/hooks/useGuestDataRefreshControl';
import useMetaData from 'app/hooks/useMetaData';
import useStore from 'app/hooks/useStore';
import { PurchasesStackParamList } from 'app/navigation/PurchasesStackNavigator';
import { AuthenticationRouteParams, RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import { tID } from 'app/services/TestHelper';

export type PurchasesScreenProps = RootScreenProps<PurchasesStackParamList, 'PurchasesList'>;

export default function PurchasesScreen({ navigation }: PurchasesScreenProps) {
  const { email, hasEntitlements, entitlements, allEntitlements } = useGuest();
  const userData = useGuestDataQuery();
  const { handleSignOut, isVerified, isUnverified } = useAuth();
  const { t } = useI18n();
  useMetaData('Purchases');
  const queryClient = useQueryClient();
  const store = useStore();
  const authRedirectSource = useStore((s) => s.authRedirectSource);
  const clearAuthRedirectSource = useStore((s) => s.clearAuthRedirectSource);
  const purchasesReturnTarget = useStore((s) => s.purchasesReturnTarget);
  const clearPurchasesReturnTarget = useStore((s) => s.clearPurchasesReturnTarget);
  const setAuthRedirectSource = useStore((s) => s.setAuthRedirectSource);
  const signOutRef = useRef(null);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [showManualAddSheet, setShowManualAddSheet] = useState(false);

  const showSignIn = isUnverified;
  const showAddTickets = !showSignIn && isVerified && !hasEntitlements;
  const showPurchases = !showAddTickets && isVerified && hasEntitlements;
  const showManualAddHeaderButton = isVerified && allEntitlements.length > 0;
  const { guestDataRefreshControl, guestDataUpdatedAt } = useGuestDataRefreshControl({
    _text: showAddTickets
      ? {
          color: 'gray.400',
        }
      : undefined,
  });

  const handleSignIn = useCallback(() => {
    setAuthRedirectSource('Purchases');
    navigation.navigate('Authentication', {
      redirectSource: 'Purchases',
    } as AuthenticationRouteParams);
  }, [navigation, setAuthRedirectSource]);
  const handleLinkTickets = useCallback(() => {
    navigation.navigate('LinkPasses');
    Analytics.trackEvent(Events.AuthManualLinkTickets);
  }, [navigation]);
  const handleManualAdd = useCallback(() => {
    setShowManualAddSheet(true);
  }, []);
  const handleManualAddClose = useCallback(() => {
    setShowManualAddSheet(false);
  }, []);
  const handleManualAddContinue = useCallback(() => {
    setShowManualAddSheet(false);
    handleLinkTickets();
  }, [handleLinkTickets]);
  const handleUserDataRefetch = useCallback(() => {
    userData.refetch();
  }, [userData]);
  const handlePurchaseTickets = useCallback(() => {
    navigation.navigate('Destinations');
  }, [navigation]);
  const handleBackToMenu = useCallback(() => {
    const returnTarget = purchasesReturnTarget;
    clearPurchasesReturnTarget();

    switch (returnTarget?.type) {
      case 'Menu':
        navigation.navigate('Menu');
        return;
      case 'Destinations':
        navigation.navigate('Destinations');
        return;
      case 'Notifications':
        navigation.navigate('Notifications');
        return;
      case 'Product':
        navigation.navigate('Tabs', {
          screen: 'ProductStack',
          params: {
            screen: 'Product',
            params: {
              productId: returnTarget.productId,
            },
          },
        });
        return;
      case 'AttractionTickets':
        navigation.navigate('Tabs', {
          screen: 'AttractionTicketsStack',
          params: {
            screen: 'AttractionTickets',
            params: {
              productId: returnTarget.productId,
            },
          },
        });
        return;
      case 'Attraction':
        navigation.navigate('Tabs', {
          screen: 'AttractionTicketsStack',
          params: {
            screen: 'Attraction',
            params: {
              productId: returnTarget.productId,
              attractionId: returnTarget.attractionId,
            },
          },
        });
        return;
      default:
        break;
    }

    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.navigate('Menu');
  }, [clearPurchasesReturnTarget, navigation, purchasesReturnTarget]);
  const handleGoToAuthentication = useCallback(() => {
    setAuthRedirectSource('Purchases');
    navigation.navigate('Authentication', {
      redirectSource: 'Purchases',
    } as AuthenticationRouteParams);
  }, [navigation, setAuthRedirectSource]);
  const handleTryDifferentEmail = useCallback(() => {
    setShowSignOutDialog(true);
  }, []);
  const handleCancelSignOut = useCallback(() => {
    setShowSignOutDialog(false);
  }, []);
  const handleConfirmSignOut = useCallback(async () => {
    const success = await handleSignOut(store, queryClient, navigation, handleGoToAuthentication);
    if (success) {
      setShowSignOutDialog(false);
    }
  }, [handleGoToAuthentication, handleSignOut, navigation, queryClient, store]);
  const handleOrderDetailsOpen = useCallback(
    (entitlementKey: string) => {
      navigation.navigate('PurchasesStack', {
        screen: 'PurchaseOrderDetails',
        params: { entitlementKey },
      });
    },
    [navigation]
  );
  const handleViewAllPastTrips = useCallback(() => {
    navigation.navigate('PurchasesStack', {
      screen: 'PastTrips',
    });
  }, [navigation]);
  const notFoundProductLineupCount = entitlements.filter(
    (ent) => ent.productLineup.productLineupKey === mismatchProductLineup
  ).length;
  const errorColor = useToken('colors', 'brand.error');
  const hasShownToastRef = useRef(false);
  const toast = useToast();
  const headerIconColor = useToken('colors', 'brand.secondary');
  useEffect(() => {
    if (authRedirectSource === 'Purchases') {
      clearAuthRedirectSource();
    }
  }, [authRedirectSource, clearAuthRedirectSource]);

  // show toast if there are entitlements with not found product lineup
  useEffect(() => {
    if (notFoundProductLineupCount > 0 && !hasShownToastRef.current) {
      toast.show({
        title: t('app_error_boundary_title'),
        description: t('app_error_retrieving_tickets'),
        duration: 5000,
        placement: 'top',
        style: { backgroundColor: errorColor },
      });
      hasShownToastRef.current = true;
    } else if (notFoundProductLineupCount === 0) {
      hasShownToastRef.current = false;
    }
  }, [errorColor, notFoundProductLineupCount, t, toast]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerStyle: {
        backgroundColor: 'white',
      },
      headerBackVisible: false,
      headerLeft: (props) => (
        <CustomHeaderBackButton {...props} labelVisible={false} onPress={handleBackToMenu} />
      ),
      headerTitle: () => (
        <Text bold fontSize="17px" testID={tID('MyPurchasesHeading')}>
          {t<string>('app_menu_my_purchases')}
        </Text>
      ),
      headerRight: isVerified
        ? () => (
            <HStack alignItems="center" space="1">
              {showManualAddHeaderButton && (
                <IconButton
                  _icon={{
                    as: TicketOutlinePlus,
                    color: headerIconColor,
                    size: '5',
                  }}
                  _pressed={{ opacity: 0.7 }}
                  borderRadius="full"
                  testID={tID('LinkTicketsHeaderButton')}
                  variant="ghost"
                  onPress={handleManualAdd}
                />
              )}
              {Platform.OS === 'web' && (
                <IconButton
                  _icon={{
                    as: PurchasesRefresh,
                    color: headerIconColor,
                    size: '5',
                  }}
                  _pressed={{ opacity: 0.7 }}
                  borderRadius="full"
                  isDisabled={userData.isFetching}
                  testID={tID('RefreshPurchasesHeaderButton')}
                  variant="ghost"
                  onPress={handleUserDataRefetch}
                />
              )}
            </HStack>
          )
        : undefined,
    });
  }, [
    handleBackToMenu,
    handleManualAdd,
    handleUserDataRefetch,
    headerIconColor,
    isVerified,
    navigation,
    showManualAddHeaderButton,
    t,
    userData.isFetching,
  ]);

  return (
    <Screen
      after={
        showSignIn || showAddTickets ? undefined : (
          <FixedBottom bg="white" pb="4" pt="2" testID={tID('PurchasesUpdatedAtFooter')}>
            <ResponsiveContainer>
              <VStack space="0" testID={tID('PurchasesUpdatedAtContent')}>
                {guestDataUpdatedAt}
              </VStack>
            </ResponsiveContainer>
          </FixedBottom>
        )
      }
      contentContainerStyle={{
        alignSelf: 'stretch',
        flexGrow: 1,
        justifyContent: showSignIn ? 'center' : undefined,
        paddingBottom: showSignIn ? 96 : undefined,
      }}
      refreshControl={guestDataRefreshControl}
      testID={tID('PurchasesScreen')}
    >
      {showSignIn && (
        <Box alignItems="center" px="4">
          <VStack alignItems="center" maxW="320px" space="4" w="full">
            <Text bold color="brand.secondary" fontSize="lg" textAlign="center">
              {t('app_auth_email_title')}
            </Text>

            <Text color="brand.secondary" fontSize="sm" textAlign="center">
              {t('app_purchases_signed_out_description')}
            </Text>

            <Button testID={tID('SignInButton')} w="full" onPress={handleSignIn}>
              {t<string>('app_auth_sign_in')}
            </Button>
          </VStack>
        </Box>
      )}

      {showAddTickets && (
        <>
          <PurchasesEmptyState
            bottomContent={
              <VStack space="0">
                {Platform.OS === 'web' && (
                  <Button
                    isLoading={userData.isFetching}
                    variant="link"
                    onPress={handleUserDataRefetch}
                  >
                    {t('app_auth_refresh_purchases')}
                  </Button>
                )}
                {guestDataUpdatedAt}
              </VStack>
            }
            email={email}
            onManualAdd={handleManualAdd}
            onPurchaseTickets={handlePurchaseTickets}
            onTryDifferentEmail={handleTryDifferentEmail}
          />

          <CPAlertDialog isOpen={showSignOutDialog} leastDestructiveRef={signOutRef}>
            <CPAlertDialog.Content testID={tID('SignOutDialog')}>
              <CPAlertDialog.Header>{`${t('app_auth_sign_out')}?`}</CPAlertDialog.Header>
              <CPAlertDialog.Body>{t('app_auth_sign_out_description')}</CPAlertDialog.Body>
              <CPAlertDialog.Footer>
                <Button testID={tID('btn-sign-out-yes')} onPress={handleConfirmSignOut}>
                  {t('gen_yes')}
                </Button>
                <Button
                  testID={tID('btn-sign-out-no')}
                  variant="link"
                  onPress={handleCancelSignOut}
                >
                  {t('gen_no')}
                </Button>
              </CPAlertDialog.Footer>
            </CPAlertDialog.Content>
          </CPAlertDialog>
        </>
      )}

      {showPurchases && (
        <Box flex={1} testID={tID('PurchasesList')}>
          <VStack flex={1} pb="20" space="6">
            <PurchasesTripsList
              email={email}
              onViewAllPastTrips={handleViewAllPastTrips}
              onViewOrderDetails={handleOrderDetailsOpen}
            />
          </VStack>
        </Box>
      )}

      <PurchasesManualAddSheet
        isOpen={showManualAddSheet}
        onClose={handleManualAddClose}
        onContinue={handleManualAddContinue}
      />
    </Screen>
  );
}
